import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Badge, Spinner } from '../components/core';
import { getAllOrgsForUser, type OrgChooserEntry } from '../services/org.service';

function orgInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function OrgChooserPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<OrgChooserEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getAllOrgsForUser(user.uid)
      .then(setOrgs)
      .catch((error) => {
        console.error('Failed to fetch user orgs:', error);
        setOrgs([]);
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => {}}
        onAvatarClick={() => navigate('/profile')}
      />

      <main className="mx-auto max-w-lg px-4 py-12 sm:px-6">
        <h1 className="mb-1 font-display text-2xl font-semibold text-gray-900">
          {t('orgChooser.title')}
        </h1>
        <p className="mb-6 font-body text-sm text-gray-500">
          {t('orgChooser.subtitle')}
        </p>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {orgs.length === 0 && (
              <p className="py-8 text-center font-body text-sm text-gray-500">
                {t('orgChooser.empty')}
              </p>
            )}

            {orgs.map((org) => (
              <button
                key={org.orgId}
                type="button"
                onClick={() => navigate(`/org/${encodeURIComponent(org.orgSlug)}`)}
                className="flex w-full items-center gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 text-start shadow-sm transition hover:border-green-400 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${
                    org.role === 'admin'
                      ? 'bg-green-200 text-green-900'
                      : 'bg-earth-light text-earth'
                  }`}
                >
                  {orgInitials(org.orgName)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body text-sm font-semibold text-gray-900">
                    {org.orgName}
                  </p>
                  <p className="truncate font-body text-xs text-gray-500">
                    org/{org.orgSlug}
                  </p>
                </div>
                <Badge variant={org.role === 'admin' ? 'default' : 'secondary'}>
                  {t(`orgChooser.role.${org.role}`)}
                </Badge>
              </button>
            ))}

            <button
              type="button"
              onClick={() => navigate('/org/new')}
              className="flex w-full items-center gap-4 rounded-xl border border-dashed border-gray-300 bg-white px-5 py-4 text-start transition hover:border-green-400 hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-2xl text-gray-400">
                +
              </div>
              <p className="font-body text-sm font-medium text-gray-600">
                {t('orgChooser.createNew')}
              </p>
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
